/**
 * Hàng chờ duyệt (Doc/11a "Luồng kiểm duyệt", E5/E6) — phần logic thuần, không DOM, không
 * gọi API, để kiểm được trong môi trường `node` của vitest.
 *
 * Một hàng chờ cho hai loại việc: phiếu chờ duyệt và đề xuất sửa dữ liệu chủ. Tạo mới
 * Hộ / Vườn / MM không vào đây (Q5).
 */
import { PROPOSAL_FIELDS } from './proposal-fields.js';

/** Bán kính "gửi xa bản ghi" — mặc định của màn Cấu hình đề xuất (Doc/11a, Quản trị). */
export const FAR_FROM_RECORD_M = 500;

/** Trường máy chủ mà bộ luật đối chiếu đọc (Q1) — chạm chúng là đổi chỉ số của phiếu. */
export function auditFieldsOf(entity) {
  return (PROPOSAL_FIELDS[entity] || []).filter((f) => f.audit).map((f) => f.key);
}

/**
 * Gom hai nguồn thành một danh sách việc.
 *
 * @param {object[]} changeRequests  `GET /change-requests?status=pending`
 * @param {object[]} submissions     phiếu `submitted` + phiếu mang cờ Cần xem lại (có thể trùng)
 */
export function buildQueue(changeRequests, submissions) {
  const items = [];
  for (const cr of changeRequests || []) {
    if (cr.status !== 'pending') continue;
    const audit = auditFieldsOf(cr.entity);
    items.push({
      key: `cr:${cr.id}`,
      type: 'cr',
      cr,
      entity: cr.entity,
      auditTouch: (cr.items || []).some((it) => audit.includes(it.field)),
      actorId: cr.requestedBy,
      actorName: cr.requester?.fullName || '',
      since: cr.requestedAt,
    });
  }
  const seen = new Set();
  for (const sub of submissions || []) {
    if (seen.has(sub.id)) continue;
    seen.add(sub.id);
    items.push({
      key: `sub:${sub.id}`,
      type: 'sub',
      sub,
      entity: sub.middlemanId ? 'middleman' : sub.plotId ? 'plot' : 'household',
      flagged: !!sub.needsReviewReason,
      errorCount: sub.errorCount || 0,
      actorId: sub.enumeratorId,
      actorName: sub.enumeratorName || '',
      since: sub.submittedAt || sub.startedAt,
    });
  }
  return items;
}

/**
 * Mức ảnh hưởng (nhỏ = lên trước), theo Doc/11a "Hàng chờ duyệt":
 *  0 — phiếu mang cờ "Cần xem lại" (đề xuất nó chốt theo đã bị từ chối);
 *  1 — đề xuất chạm trường đối chiếu (diện tích, loại đất): đổi chỉ số của phiếu;
 *  2 — phiếu còn lỗi đối chiếu mức đỏ;
 *  3 — còn lại.
 */
export function impactRank(item) {
  if (item.type === 'sub' && item.flagged) return 0;
  if (item.type === 'cr' && item.auditTouch) return 1;
  if (item.type === 'sub' && item.errorCount > 0) return 2;
  return 3;
}

/** Theo mức ảnh hưởng, rồi việc chờ LÂU hơn lên trước. Không đổi mảng đầu vào. */
export function sortQueue(items) {
  return [...items].sort((a, b) => {
    const r = impactRank(a) - impactRank(b);
    if (r) return r;
    return new Date(a.since || 0) - new Date(b.since || 0);
  });
}

/**
 * @param {object[]} items
 * @param {{type?: 'all'|'cr'|'sub', entity?: string, actorId?: string}} f
 */
export function filterQueue(items, f = {}) {
  return items.filter(
    (it) =>
      (!f.type || f.type === 'all' || it.type === f.type) &&
      (!f.entity || it.entity === f.entity) &&
      (!f.actorId || it.actorId === f.actorId),
  );
}

/** "3 ngày", "5 giờ", "12 phút" — đã chờ bao lâu. */
export function waitedText(since, now = Date.now()) {
  const ms = now - new Date(since).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${Math.max(min, 1)} phút`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} giờ`;
  return `${Math.floor(h / 24)} ngày`;
}

/** Khoảng cách mặt cầu (m) giữa hai điểm, `null` nếu thiếu toạ độ. */
export function distanceM(a, b) {
  const nums = [a?.lat, a?.lng, b?.lat, b?.lng].map((v) => (v === null || v === undefined || v === '' ? NaN : Number(v)));
  if (nums.some((v) => !Number.isFinite(v))) return null;
  const [lat1, lng1, lat2, lng2] = nums.map((v) => (v * Math.PI) / 180);
  const h = Math.sin((lat2 - lat1) / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin((lng2 - lng1) / 2) ** 2;
  return Math.round(2 * 6371000 * Math.asin(Math.sqrt(h)));
}

/** "gửi cách vườn 40 m" / "12,3 km" */
export function distanceText(m) {
  if (m === null) return '';
  return m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1).replace('.', ',')} km`;
}

function asText(v) {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v);
  // Decimal của Prisma về dạng "1.5000" — so như máy chủ (`currentValueText`).
  return /^-?\d+\.\d+$/.test(s) ? String(Number(s)) : s;
}

/**
 * Trường có giá trị hiện hành đã khác giá trị lúc gửi — máy chủ sẽ trả
 * `STALE_CHANGE_REQUEST` khi duyệt. Hiện trước để người duyệt không bị bất ngờ (E6).
 */
export function staleItems(cr, record) {
  if (!record) return [];
  return (cr.items || []).filter((it) => asText(it.oldValue) !== asText(record[it.field]));
}

/** Giá trị hiện hành dạng chữ, cho cột "Hiện hành". */
export function currentText(record, field) {
  return asText(record?.[field]);
}

/**
 * Phiếu có cùng chủ thể với một đề xuất đang chờ — để dòng "Chủ thể có n đề xuất chờ
 * duyệt" ở phiếu (Doc/11a "Duyệt phiếu"): xử lý đề xuất trước khi duyệt phiếu.
 */
export function pendingForSubmission(sub, changeRequests) {
  return (changeRequests || []).filter(
    (cr) =>
      cr.status === 'pending' &&
      ((cr.entity === 'household' && cr.entityId === sub.householdId) ||
        (cr.entity === 'plot' && cr.entityId === sub.plotId) ||
        (cr.entity === 'middleman' && cr.entityId === sub.middlemanId)),
  );
}
